import { useCallback, useMemo } from "react";
import { useFileExplorerSearchParams } from "../file-explorer/useFileExplorerSearchParams";
import { isPdf } from "../../utils/fileUtils";
import type { FileItem } from "../../types";

function isViewableFile(file: FileItem): boolean {
  return file.type === "image" || isPdf(file);
}

interface UseImageDialogNavigationOptions {
  allFiles: FileItem[];
}

export function useImageDialogNavigation({
  allFiles,
}: UseImageDialogNavigationOptions) {
  const { search, updateSearchParams } = useFileExplorerSearchParams();

  const viewableFiles = useMemo(
    () => allFiles.filter(isViewableFile),
    [allFiles]
  );

  const isOpen = search["file-view"] === "detail" && !!search.fileId;
  const viewerFile = useMemo(() => {
    if (!isOpen || !search.fileId) return null;
    const file = allFiles.find((f) => f.id === search.fileId);
    return file && isViewableFile(file) ? file : null;
  }, [isOpen, search.fileId, allFiles]);

  const handleViewerClick = useCallback(
    (file: FileItem) => {
      updateSearchParams({ fileId: file.id, "file-view": "detail" });
    },
    [updateSearchParams]
  );

  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        updateSearchParams({ "file-view": undefined });
      }
    },
    [updateSearchParams]
  );

  const handleDialogNavigate = useCallback(
    (direction: "prev" | "next") => {
      if (!viewerFile) return;

      const currentIndex = viewableFiles.findIndex(
        (f) => f.id === viewerFile.id
      );

      let targetFile: FileItem | null = null;
      if (direction === "prev" && currentIndex > 0) {
        targetFile = viewableFiles[currentIndex - 1];
      } else if (
        direction === "next" &&
        currentIndex < viewableFiles.length - 1
      ) {
        targetFile = viewableFiles[currentIndex + 1];
      }

      if (targetFile) {
        updateSearchParams({
          fileId: targetFile.id,
          "file-view": "detail",
        });
      }
    },
    [viewerFile, viewableFiles, updateSearchParams]
  );

  return {
    imageDialog: {
      isOpen: isOpen && viewerFile !== null,
      imageFile: viewerFile,
    },
    handleImageClick: handleViewerClick,
    handleImageDialogOpenChange: handleDialogOpenChange,
    handleImageDialogNavigate: handleDialogNavigate,
  };
}
